import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { formatPhone } from "shared/utils";
import { crmAnalyticsApi, type DateRangeParams } from "api/crm-analytics";
import { getErrorMessage } from "utils/error";
import { AnalyticsDetailModal } from "../AnalyticsDetailModal";
import { DrilldownRow } from "../DrilldownRow";
import type { UnitFilterParams } from "../UnitsFilter";
import {
  DEFAULT_PER_PAGE,
  DRILLDOWN_ONE_DATE_GRID_CLASSNAME
} from "../../constants";
import type { JourneySelection } from "./graph";

const COLUMNS = [
  { label: "Name" },
  { label: "Property" },
  { label: "Unit" },
  { label: "Date created" }
];

const subtitleFor = (selection: JourneySelection) =>
  `${selection.count.toLocaleString("en-US")} ${
    selection.count === 1 ? "lead" : "leads"
  }`;

/**
 * The leads behind one bar or ribbon of the Lead journey.
 *
 * The id goes back to the API untouched, so the list is resolved by the same
 * cohort and bitmask rules that produced the number on the chart and the two
 * cannot drift apart — a ribbon included. Every lead here was created inside
 * the selected range, so "Date created" is the column that makes sense for
 * anything that can be clicked.
 */
export const LeadJourneyModal = ({
  selection,
  onClose,
  dateParams,
  filterParams
}: {
  selection: JourneySelection | null;
  onClose: () => void;
  dateParams: DateRangeParams;
  filterParams?: UnitFilterParams;
}) => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [selection?.id, dateParams, filterParams]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      "CRM_ANALYTICS_LEAD_JOURNEY_DRILLDOWN",
      selection?.id,
      dateParams,
      filterParams,
      page
    ],
    queryFn: () =>
      crmAnalyticsApi.getLeadJourneyDrilldown({
        node_id: selection!.id,
        ...dateParams,
        ...filterParams,
        page,
        per_page: DEFAULT_PER_PAGE
      }),
    enabled: selection !== null,
    placeholderData: keepPreviousData,
    meta: {
      onError: (err: unknown) => toast.error(getErrorMessage(err))
    }
  });

  const leads = data?.items ?? [];

  const openLead = (leadId: number) => {
    onClose();
    navigate(`/leads/${leadId}`);
  };

  return (
    <AnalyticsDetailModal
      opened={selection !== null}
      onClose={onClose}
      title={selection?.label ?? ""}
      subtitle={selection ? subtitleFor(selection) : undefined}
      gridClassName={DRILLDOWN_ONE_DATE_GRID_CLASSNAME}
      columns={COLUMNS}
      isLoading={isLoading}
      isFetching={isFetching}
      isEmpty={leads.length === 0}
      emptyText="No leads to show"
      pagination={{
        total: data?.page_count ?? 0,
        current: page,
        onChange: setPage
      }}
    >
      {leads.map((lead) => (
        <DrilldownRow
          key={lead.id}
          name={`${lead.first_name} ${lead.last_name}`}
          contactDetail={
            lead.primary_phone
              ? formatPhone(lead.primary_phone)
              : lead.primary_email
          }
          propertyName={lead.property?.name ?? null}
          propertyAddress={lead.property?.address.address1 ?? null}
          unitTitle={lead.unit?.title ?? null}
          onClick={() => openLead(lead.id)}
          gridClassName={DRILLDOWN_ONE_DATE_GRID_CLASSNAME}
          dates={[{ value: lead.created_at }]}
        />
      ))}
    </AnalyticsDetailModal>
  );
};
